import React, { useCallback, useEffect, useState } from "react";
import { createSelector } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import { Radio } from "@mui/material";
import { CustomDialog } from "../../../../../../common/components";
import { translate } from "../../../../../../common/providers";
import styles from "./RedressAssociate.module.css";
import { useApi } from "../../../../../../common/hooks";
import { ContentService, ReqViewService } from "../../../../../../api";
import InformationBlock from "./InformationBlock";

const selectPageContents = createSelector(
	[(state) => state.srPdf.content, (_, pageStart) => pageStart],
	(content, pageStart) => {
		if (!pageStart) {
			return [];
		}
		const pages = new Set([pageStart - 1, pageStart, pageStart + 1]);
		return content.filter((p) => pages.has(p.page)).map(({ page, req }) => ({ page, req }));
	}
);

const RedressAssociate = ({ block, open, onClose }) => {
	const [selectedInformation, setSelectedInformation] = useState(null);
	const [currentInformation, setCurrentInformation] = useState({});
	const [previousInformation, setPreviousInformation] = useState({});
	const [nextInformation, setNextInformation] = useState({});
	const docId = useSelector(({ srDocument }) => srDocument.documentId);
	const numberOfPages = useSelector(({ srDocument }) => srDocument.numberOfPages);
	const pageContents = useSelector((state) => selectPageContents(state, block?.pageStart));
	const { call: getContents } = useApi(ReqViewService.getContents);
	const { call: getAllChapterContents } = useApi(ContentService.getAllChapterContents);
	const fetchInformation = useCallback(
		async (documentId, informationId) => {
			if (!documentId || !informationId) {
				return null;
			}
			try {
				const content = await getAllChapterContents({ docId: documentId, infoId: informationId });
				return { informationId, content };
			} catch (err) {
				console.error("Error fetching chapter contents:", err);
				return null;
			}
		},
		[getAllChapterContents]
	);
	const loadInformation = useCallback(async () => {
		if (!open || !block?.contentId || !docId) {
			return;
		}
		try {
			const pageIndex = block.pageStart;
			let currentPage = pageContents.find((p) => p.page === pageIndex);
			if (!currentPage || !currentPage.req) {
				const fetchedPage = await getContents({ docId }, { page: pageIndex });
				currentPage = { page: pageIndex, req: fetchedPage.informations };
			}
			const reqs = currentPage.req || [];
			if (reqs.length === 0) {
				return;
			}
			const currentReq = reqs.find((info) => info.rectangles.some((r) => r.contentId === block.contentId));
			if (!currentReq) {
				return;
			}
			setSelectedInformation(currentReq.informationId);
			const currentInformationData = await fetchInformation(docId, currentReq.informationId);
			setCurrentInformation({ ...currentReq, content: currentInformationData?.content });
			const rects = currentReq.rectangles;
			if (rects[0].contentId === block.contentId) {
				let prevReq = null;
				const currentIndex = reqs.indexOf(currentReq);
				if (currentIndex > 0) {
					prevReq = reqs[currentIndex - 1];
				} else if (block.pageStart > 1) {
					let prevPage = pageContents.find((p) => p.page === pageIndex - 1);
					if (!prevPage || !prevPage.req) {
						const fetchedPrev = await getContents({ docId }, { page: pageIndex - 1 });
						prevPage = { page: pageIndex - 1, req: fetchedPrev.informations };
					}
					prevReq = prevPage.req?.[prevPage.req.length - 1] || null;
				}
				if (prevReq) {
					const prevData = await fetchInformation(docId, prevReq.informationId);
					setPreviousInformation({ ...prevReq, content: prevData?.content });
				}
			}
			if (rects.at(-1).contentId === block.contentId) {
				let nextReq = null;
				const currentIndex = reqs.indexOf(currentReq);
				if (currentIndex < reqs.length - 1) {
					nextReq = reqs[currentIndex + 1];
				} else if (block.pageStart < numberOfPages) {
					let nextPage = pageContents.find((p) => p.page === pageIndex + 1);
					if (!nextPage || !nextPage.req) {
						const fetchedNext = await getContents({ docId }, { page: pageIndex + 1 });
						nextPage = { page: pageIndex + 1, req: fetchedNext.informations };
					}
					nextReq = nextPage.req?.[0] || null;
				}
				if (nextReq) {
					const nextData = await fetchInformation(docId, nextReq.informationId);
					setNextInformation({ ...nextReq, content: nextData?.content });
				}
			}
		} catch (err) {
			console.error("Error loading information:", err);
		}
	}, [open, block, docId, numberOfPages, pageContents, getContents, fetchInformation]);
	useEffect(() => {
		loadInformation();
	}, [loadInformation]);
	useEffect(() => {
		if (!open) {
			setCurrentInformation({});
			setNextInformation({});
			setPreviousInformation({});
			setSelectedInformation(null);
		}
	}, [open]);
	const handleSelect = (req) => {
		if (!req) {
			setSelectedInformation(null);
		} else {
			setSelectedInformation(req.informationId);
		}
	};
	const handleSubmit = () => {
		onClose();
	};
	return (
		<CustomDialog
			className={styles.dialog}
			open={open}
			submitDisabled={selectedInformation === currentInformation.informationId}
			submitLabel={translate("common:btn.confirm")}
			subTitle={
				<div className={styles.container}>
					<div>{translate("smart-review.redress-associate.dialog.subtitle")}</div>
					<div>{translate("smart-review.redress-associate.dialog.create-option")}</div>
				</div>
			}
			title={translate("smart-review.redress-associate.dialog.title")}
			onClose={onClose}
			onSubmit={handleSubmit}
		>
			{currentInformation && (
				<div className={styles.informationContainer}>
					<InformationBlock
						information={previousInformation}
						labelTranslateKey="smart-review.redress-associate.dialog.previous-information"
						selectedInformation={selectedInformation}
						onSelect={handleSelect}
					/>
					<InformationBlock
						disabled
						information={currentInformation}
						labelTranslateKey="smart-review.redress-associate.dialog.current-information"
						selectedInformation={selectedInformation}
						onSelect={handleSelect}
					/>
					<InformationBlock
						information={nextInformation}
						labelTranslateKey="smart-review.redress-associate.dialog.next-information"
						selectedInformation={selectedInformation}
						onSelect={handleSelect}
					/>
				</div>
			)}
			{!currentInformation && (
				<div className={styles.newInformation}>
					<div>
						<Radio checked={selectedInformation === null} onChange={() => handleSelect(null)} />
					</div>
					<div>{translate("smart-review.redress-associate.dialog.create-option")}</div>
				</div>
			)}
		</CustomDialog>
	);
};

export default RedressAssociate;
